import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr, downloadCSV, downloadPDF, whatsappShare } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, FileText, FileSpreadsheet, MessageCircle, Search,
  Filter as FilterIcon, ChevronDown, ChevronRight, Printer
} from "lucide-react";
import { PageHeader, ExportBar } from "@/components/EntityPage";

const CHARGE_FIELDS = [
  ["fuel_charge","Fuel"], ["driver_charge","Driver"], ["maintenance","Maintenance"],
  ["paper_fees","Paper Fees"], ["parking_fees","Parking"], ["road_side","Road-side"],
];

// All fields shown per-row and per-column exports
const FIELDS = [
  { key: "trip_id",             label: "Trip ID",        tone: "amber" },
  { key: "entry_date",          label: "Entry Date" },
  { key: "vehicle_reg",         label: "Vehicle",        tone: "sky" },
  { key: "vendor_name",         label: "Vendor" },
  { key: "client_name",         label: "Client" },
  { key: "driver_name",         label: "Driver" },
  { key: "start_date",          label: "Start" },
  { key: "end_date",            label: "End" },
  { key: "no_of_days",          label: "No. of Days" },
  { key: "start_km",            label: "Start KM" },
  { key: "end_km",              label: "End KM" },
  { key: "km_difference",       label: "KM Difference",  tone: "amber" },
  { key: "fuel_charge",         label: "Fuel Charge",    money: true },
  { key: "driver_charge",       label: "Driver Charge",  money: true },
  { key: "maintenance",         label: "Maintenance",    money: true },
  { key: "paper_fees",          label: "Paper Fees",     money: true },
  { key: "parking_fees",        label: "Parking Fees",   money: true },
  { key: "road_side",           label: "Road-side",      money: true },
  { key: "total_charges",       label: "Total Charges",  money: true, tone: "red" },
  { key: "advance_paid_driver", label: "Advance to Driver", money: true },
  { key: "remaining_balance",   label: "Remaining Balance", money: true },
  { key: "trip_value",          label: "Trip Value",     money: true, tone: "sky" },
  { key: "payable_to_driver",   label: "Payable to Driver", money: true, tone: "emerald" },
  { key: "receivable_from_driver", label: "Receivable from Driver", money: true, tone: "pink" },
  { key: "savings_deduct",      label: "Savings Deduction", money: true },
  { key: "profit_loss",         label: "Profit / Loss",  money: true, tone: "pl" },
  { key: "remarks",             label: "Remarks" },
  { key: "sales_agent_name",    label: "Sales Agent",    tone: "pink" },
];

function cellText(row, c) {
  const v = row[c.key];
  if (v === undefined || v === null || v === "") return "—";
  if (c.money) return fmt(v);
  return String(v);
}
function toneClass(c, row) {
  if (c.tone === "amber") return "text-amber-300 font-mono";
  if (c.tone === "sky") return "text-sky-300 font-mono";
  if (c.tone === "red") return "text-red-300 font-mono";
  if (c.tone === "emerald") return "text-emerald-300 font-mono";
  if (c.tone === "pink") return "text-pink-300 font-mono";
  if (c.tone === "pl") return `font-mono ${row.profit_loss >= 0 ? "text-emerald-300" : "text-red-300"}`;
  if (c.money) return "font-mono";
  return "";
}
function rowClass(r) {
  const pay = (r.payable_to_driver || 0) > 0;
  const recv = (r.receivable_from_driver || 0) > 0;
  if (pay && recv) return "bg-amber-500/[0.06] border-l-4 border-l-amber-500/60";
  if (pay) return "bg-rose-500/[0.06] border-l-4 border-l-rose-500/60";
  if (recv) return "bg-sky-500/[0.06] border-l-4 border-l-sky-500/60";
  return "bg-transparent border-l-4 border-l-transparent";
}

function TripForm({ initial, onSubmit, refs }) {
  const [f, setF] = useState(initial || {});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (k) => (e) => set(k, parseFloat(e.target.value) || 0);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        {[
          ["vehicle_id","Vehicle",refs.vehicles,(v)=>`${v.registration_no} · ${v.vehicle_name}`],
          ["driver_id","Driver",refs.drivers,(v)=>v.name],
          ["client_id","Client",refs.clients,(v)=>v.name],
          ["vendor_id","Vendor",refs.vendors,(v)=>v.name],
        ].map(([k,l,arr,fmtl]) => (
          <div key={k}>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Select value={f[k] || ""} onValueChange={(v) => set(k, v)}>
              <SelectTrigger data-testid={`trip-${k}`} className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder={`Select ${l.toLowerCase()}`}/></SelectTrigger>
              <SelectContent>{arr.map((it) => <SelectItem key={it.id} value={it.id}>{fmtl(it)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["entry_date","Entry"],["start_date","Start"],["end_date","End"]].map(([k,l]) => (
          <div key={k}><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Input type="date" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        ))}
        {[["start_km","Start KM"],["end_km","End KM"],["trip_value","Trip value (₹)"]].map(([k,l]) => (
          <div key={k}><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Input type="number" value={f[k] || 0} onChange={num(k)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {CHARGE_FIELDS.map(([k, l]) => (
          <div key={k}><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Input type="number" value={f[k] || 0} onChange={num(k)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        ))}
        {[["advance_paid_driver","Advance to driver"],["advance_received","Advance received"],["savings_deduct","Savings deduct"]].map(([k,l]) => (
          <div key={k}><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Input type="number" value={f[k] || 0} onChange={num(k)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        ))}
      </div>
      <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Remarks</Label>
        <Input value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>

      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Sales agent (attribution for commission)</Label>
        <Select value={f.sales_agent_id || "none"} onValueChange={(v) => set("sales_agent_id", v === "none" ? null : v)}>
          <SelectTrigger data-testid="trip-sales-agent" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="No sales agent"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— No sales agent —</SelectItem>
            {(refs.salesAgents || []).map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.name} · {a.employee_id} · {parseFloat(a.commission_pct || 0).toFixed(2)}%</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[10px] text-muted-foreground mt-1">Commission on this trip = <span className="text-emerald-300">Profit</span> × agent %. Leave blank if no agent is attributed.</div>
      </div>

      <div className="mt-2 p-3 rounded-md border border-amber-500/25 bg-amber-500/[0.04] text-[11px] text-amber-200/90">
        Client-facing details (T&C, itinerary, assigned vehicle & driver, total trip cost, 4-step payment plan) are managed in <span className="font-semibold">Client Assignments</span>. Save this trip first, then open it there to configure the customer view.
      </div>

      <div className="flex justify-end">
        <Button type="submit" data-testid="trip-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Save Trip</Button>
      </div>
    </form>
  );
}

function TripRow({ r, onEdit, onDelete, onPrint }) {
  const [open, setOpen] = useState(false);
  const summary = [
    { label: "Trip ID", val: r.trip_id, cls: "text-amber-300 font-mono" },
    { label: "Date", val: r.entry_date || r.start_date || "—" },
    { label: "Vehicle", val: r.vehicle_reg || "—", cls: "text-sky-300 font-mono" },
    { label: "Driver", val: r.driver_name || "—" },
    { label: "KM Diff", val: r.km_difference || 0, cls: "text-amber-300 font-mono" },
    { label: "Trip Value", val: fmt(r.trip_value), cls: "text-sky-300 font-mono" },
    { label: "P / L", val: (r.profit_loss >= 0 ? "+" : "") + fmt(r.profit_loss),
      cls: `font-mono ${r.profit_loss >= 0 ? "text-emerald-300" : "text-red-300"}` },
  ];
  return (
    <div className={`rounded-lg border border-border transition-colors ${rowClass(r)}`} data-testid={`trip-row-${r.id}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-4">
        <div className="text-slate-400">
          {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-center">
          {summary.map((s) => (
            <div key={s.label} className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{s.label}</div>
              <div className={`text-sm truncate ${s.cls || ""}`}>{s.val}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {r.payable_to_driver > 0 && <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40">Payable · {fmt(r.payable_to_driver)}</Badge>}
          {r.receivable_from_driver > 0 && <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/40">Recv · {fmt(r.receivable_from_driver)}</Badge>}
          {!r.payable_to_driver && !r.receivable_from_driver && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Settled</Badge>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-border/60">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 pt-3">
            {FIELDS.map((c) => (
              <div key={c.key} className="grid grid-cols-[130px_1fr] items-center py-1 border-b border-border/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                <div className={`text-sm ${toneClass(c, r)}`}>{cellText(r, c)}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-3 justify-end">
            <Button variant="outline" size="sm" data-testid={`trip-slip-${r.id}`} onClick={() => onPrint(r)} className="gap-2 border-border bg-[hsl(224_40%_12%)]">
              <Printer className="w-3.5 h-3.5"/> Trip slip (PDF)
            </Button>
            <Button variant="outline" size="sm" data-testid={`edit-trip-${r.id}`} onClick={() => onEdit(r)} className="gap-2 border-border bg-[hsl(224_40%_12%)]">
              <Pencil className="w-3.5 h-3.5"/> Edit
            </Button>
            <Button variant="outline" size="sm" data-testid={`delete-trip-${r.id}`} onClick={() => onDelete(r.id)} className="gap-2 border-red-500/40 text-red-300 hover:bg-red-500/10">
              <Trash2 className="w-3.5 h-3.5"/> Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Row-wise PDF: one trip per printed section with Field / Value stack
function slipPDF(r) {
  const w = window.open("", "_blank");
  const style = `<style>body{font-family:sans-serif;padding:24px;color:#0b1220}
    h1{margin:0 0 4px}.sub{color:#64748b;font-size:12px;margin-bottom:16px}
    table{border-collapse:collapse;width:100%;font-size:12px}
    th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left;vertical-align:top}
    th{background:#f8fafc;width:36%;color:#334155;font-weight:600}</style>`;
  const rows = FIELDS.map((c) => `<tr><th>${c.label}</th><td>${cellText(r, c)}</td></tr>`).join("");
  w.document.write(`<html><head><title>Trip ${r.trip_id}</title>${style}</head><body>
    <h1>Trip Slip · ${r.trip_id}</h1><div class="sub">Ashok Netravels · ${r.entry_date || ""}</div>
    <table>${rows}</table>
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`);
  w.document.close();
}

function ledgerPDF(rows) {
  if (!rows.length) return;
  const w = window.open("", "_blank");
  const style = `<style>body{font-family:sans-serif;padding:20px;color:#0b1220}
    h1{margin:0 0 10px}
    table{border-collapse:collapse;width:100%;font-size:10px;margin-bottom:18px;page-break-inside:avoid}
    th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;vertical-align:top}
    th{background:#f1f5f9;width:35%;color:#334155;font-weight:600}
    h2{font-size:14px;margin:20px 0 6px;color:#0f172a;border-bottom:2px solid #f59e0b;padding-bottom:4px}</style>`;
  const sections = rows.map((r) => {
    const body = FIELDS.map((c) => `<tr><th>${c.label}</th><td>${cellText(r, c)}</td></tr>`).join("");
    return `<h2>${r.trip_id} · ${r.entry_date || ""}</h2><table>${body}</table>`;
  }).join("");
  w.document.write(`<html><head><title>Trips Ledger</title>${style}</head><body>
    <h1>Ashok Netravels · Trips Ledger (${rows.length})</h1>${sections}
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`);
  w.document.close();
}

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [refs, setRefs] = useState({ vehicles: [], drivers: [], clients: [], vendors: [], salesAgents: [] });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fDriver, setFDriver] = useState("all");
  const [fVehicle, setFVehicle] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  async function loadAll() {
    const [t, v, d, c, vd, sa] = await Promise.all([
      api.get("/trips"), api.get("/vehicles"), api.get("/drivers"), api.get("/clients"), api.get("/vendors"),
      api.get("/sales-agents").catch(() => ({ data: [] })),
    ]);
    setTrips(t.data);
    setRefs({ vehicles: v.data, drivers: d.data, clients: c.data, vendors: vd.data, salesAgents: sa.data });
  }
  useEffect(() => { loadAll(); }, []);

  const nameOf = (arr, id, key = "name") => arr.find((x) => x.id === id)?.[key] || "";
  const enriched = useMemo(() => trips.map((t) => ({
    ...t,
    km_difference: Math.max(0, (t.end_km || 0) - (t.start_km || 0)),
    vehicle_reg: nameOf(refs.vehicles, t.vehicle_id, "registration_no"),
    driver_name: nameOf(refs.drivers, t.driver_id),
    client_name: nameOf(refs.clients, t.client_id),
    vendor_name: nameOf(refs.vendors, t.vendor_id),
    sales_agent_name: (() => {
      const a = (refs.salesAgents || []).find((x) => x.id === t.sales_agent_id);
      return a ? `${a.name} (${a.employee_id})` : "";
    })(),
  })), [trips, refs]);

  const filtered = useMemo(() => enriched.filter((r) => {
    if (fDriver !== "all" && r.driver_id !== fDriver) return false;
    if (fVehicle !== "all" && r.vehicle_id !== fVehicle) return false;
    const d = (r.entry_date || r.start_date || "").slice(0, 10);
    if (fFrom && (!d || d < fFrom)) return false;
    if (fTo && (!d || d > fTo)) return false;
    if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [enriched, fDriver, fVehicle, fFrom, fTo, q]);

  const totals = useMemo(() => filtered.reduce((a, t) => ({
    revenue: a.revenue + (t.trip_value || 0),
    expense: a.expense + (t.total_charges || 0),
    profit: a.profit + (t.profit_loss || 0),
    payable: a.payable + (t.payable_to_driver || 0),
    receivable: a.receivable + (t.receivable_from_driver || 0),
  }), { revenue: 0, expense: 0, profit: 0, payable: 0, receivable: 0 }), [filtered]);

  async function save(f) {
    try { editing ? await api.put(`/trips/${editing.id}`, f) : await api.post("/trips", f);
      toast.success("Trip saved"); setOpen(false); setEditing(null); loadAll();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/trips/${id}`); loadAll(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  const exportRows = filtered.map((t) => Object.fromEntries(FIELDS.map((c) => [c.label, cellText(t, c)])));
  const clearFilters = () => { setFDriver("all"); setFVehicle("all"); setFFrom(""); setFTo(""); setQ(""); };
  const activeFilters = (fDriver !== "all") + (fVehicle !== "all") + !!fFrom + !!fTo + !!q;

  return (
    <div>
      <PageHeader
        title="Trips & Accounting"
        subtitle="Row-wise ledger. Click any trip to expand full details. Payable / receivable rows are colour-coded."
        right={
          <>
            <Button variant="outline" data-testid="ledger-pdf" onClick={() => ledgerPDF(filtered)} className="border-border bg-[hsl(224_40%_12%)] hover:bg-white/5 gap-2"><FileText className="w-4 h-4"/> Row-wise PDF</Button>
            <ExportBar rows={exportRows} filename="trips-ledger" whatsappText={`Trips ledger · ${filtered.length} trips · Net ₹${totals.profit}`}/>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-trip-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4" /> New Trip</Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-3xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Trip" : "New Trip"}</DialogTitle></DialogHeader>
                <TripForm initial={editing} onSubmit={save} refs={refs} />
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Revenue</div>
          <div className="font-display text-2xl font-bold text-sky-300 mt-1">{fmt(totals.revenue)}</div>
        </Card>
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Expense</div>
          <div className="font-display text-2xl font-bold text-red-300 mt-1">{fmt(totals.expense)}</div>
        </Card>
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Net Profit</div>
          <div className={`font-display text-2xl font-bold mt-1 ${totals.profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{totals.profit >= 0 ? "+" : ""}{fmt(totals.profit)}</div>
        </Card>
        <Card className="p-4 bg-rose-500/[0.08] border-rose-500/30" data-testid="totals-payable">
          <div className="text-[11px] uppercase tracking-wider text-rose-300">Total payable → driver</div>
          <div className="font-display text-2xl font-bold text-rose-200 mt-1">{fmt(totals.payable)}</div>
        </Card>
        <Card className="p-4 bg-sky-500/[0.08] border-sky-500/30" data-testid="totals-receivable">
          <div className="text-[11px] uppercase tracking-wider text-sky-300">Total receivable ← driver</div>
          <div className="font-display text-2xl font-bold text-sky-200 mt-1">{fmt(totals.receivable)}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-4">
        <div className="flex items-center gap-2 mb-4 text-amber-400">
          <FilterIcon className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Filters</div>
          {activeFilters > 0 && <button data-testid="clear-all-filters" onClick={clearFilters} className="ml-auto text-[11px] text-amber-300 hover:underline">Clear all</button>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Driver</Label>
            <Select value={fDriver} onValueChange={setFDriver}>
              <SelectTrigger data-testid="f-driver" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {refs.drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle</Label>
            <Select value={fVehicle} onValueChange={setFVehicle}>
              <SelectTrigger data-testid="f-vehicle" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no} · {v.vehicle_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From date</Label>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="f-from" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To date</Label>
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="f-to" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
        </div>
      </Card>

      {/* Row list */}
      <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text…" data-testid="trip-search" className="bg-transparent border-0 focus-visible:ring-0 h-8 max-w-xs"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {trips.length}</div>
        </div>
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No trips match your filters</div>
          ) : filtered.map((r) => (
            <TripRow key={r.id} r={r}
              onEdit={(x) => { setEditing(x); setOpen(true); }}
              onDelete={del} onPrint={slipPDF}/>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-rose-500/60 bg-rose-500/10"/> Payable to driver</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-sky-500/60 bg-sky-500/10"/> Receivable from driver</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-amber-500/60 bg-amber-500/10"/> Both</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-transparent bg-white/[0.03]"/> Settled / normal</span>
        </div>
      </Card>
    </div>
  );
}
